/**
 * Finds the portal container element for MUI components (e.g., Popover, Modal) within Shadow DOM or regular DOM.
 * In Shadow DOM MFEs, we need to search within the shadow root, not the main document.
 *
 * This function is memoized to cache the container element after first lookup, avoiding expensive
 * DOM traversals on subsequent calls. MUI may call this function multiple times during renders.
 */
let cachedContainer: HTMLElement | null | undefined = undefined;

const getMfeContainer = (): HTMLElement | null => {
  // Return cached result if available
  if (cachedContainer !== undefined) {
    // Verify cached container is still valid
    // For shadow DOM elements, check if element still exists and has a root node
    if (cachedContainer && cachedContainer.isConnected) {
      return cachedContainer;
    }
  }

  // Strategy 1: Try regular document lookup first (non-shadow DOM scenario)
  const regularContainer = document.getElementById('pos-app-root');
  if (regularContainer) {
    cachedContainer = regularContainer;
    return regularContainer;
  }

  // Strategy 2: Look for shadow root that contains our app
  // Traverse document to find shadow hosts using TreeWalker (more efficient)
  const walker = document.createTreeWalker(
    document.body || document.documentElement,
    NodeFilter.SHOW_ELEMENT,
    null
  );

  let node: Node | null = walker.currentNode;

  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      // Check if this element has a shadow root
      if (element.shadowRoot) {
        // Check for pos-ui-app-root inside shadow DOM
        const container = element.shadowRoot.getElementById('pos-app-root');
        if (container) {
          cachedContainer = container;
          return container;
        }

        // Also check for data-shadow-app-root element (fallback)
        const shadowAppRoot = element.shadowRoot.querySelector(
          '[data-shadow-app-root]'
        ) as HTMLElement;
        if (shadowAppRoot) {
          cachedContainer = shadowAppRoot;
          return shadowAppRoot;
        }
      }
    }

    node = walker.nextNode();
  }

  // Strategy 3 removed: document.querySelectorAll('*') is inefficient and redundant.
  // TreeWalker in Strategy 2 already traverses all elements efficiently.

  // Final fallback: Cache null result to avoid repeated searches
  // Return null - MUI will use document.body
  // Note: We cache null so we don't repeatedly search when no container exists
  cachedContainer = null;
  return null;
};

/**
 * Resets the cached container. Useful for testing or when the DOM structure changes.
 */
export const resetMfeContainerCache = (): void => {
  cachedContainer = undefined;
};

export default getMfeContainer;
